import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { AiOutlineClose } from 'react-icons/ai';

import { getLandingPopup } from '@/apis/notices';
import { usePopupStore } from '@/store/popup';
import { QUERY_KEY } from '@/constants/queryKey';
import { Theme } from '@/styles/Theme';

const POPUP_WIDTH = '22rem';
const POPUP_HEIGHT = '35.9375rem';

const LandingPopup = () => {
  const { popups, setPopupVisible, setHidePopupUntil, checkVisibility } =
    usePopupStore();

  const { data: landingData } = useQuery({
    queryKey: [QUERY_KEY.LANDING_POPUP],
    queryFn: getLandingPopup,
  });

  const notice = landingData?.data ?? null;

  useEffect(() => {
    if (notice?.id) {
      checkVisibility([notice.id]);
    }
  }, [notice?.id, checkVisibility]);

  if (!notice) return null;

  const popupState = popups.find((p) => p.id === notice.id);
  const isVisible = popupState?.isPopupVisible ?? true;

  if (!isVisible) return null;

  const handleClose = () => {
    setHidePopupUntil(notice.id, null);
    setPopupVisible(notice.id, false);
  };

  const { title, content, image } = notice;
  const hasImage = image && image.length > 0;

  return (
    <Container>
      <Wrapper>
        <TitleHeader>
          <TitleText>{title}</TitleText>
          <CloseIconWrapper onClick={handleClose}>
            <AiOutlineClose color={Theme.colors.B_3_light} size='1.5rem' />
          </CloseIconWrapper>
        </TitleHeader>
        <ContentWrapper>
          <ScrollArea>
            <ContentText fontSize='0.9375rem'>{content}</ContentText>
            {hasImage && (
              <ImgList>
                {image.map((img, idx) => (
                  <img key={idx} src={img} alt={`notice_img_${idx}`} />
                ))}
              </ImgList>
            )}
          </ScrollArea>
        </ContentWrapper>
      </Wrapper>
    </Container>
  );
};

export default LandingPopup;

const Container = styled.div`
  z-index: 300;
  position: fixed;
  top: 8.75rem;
  left: 1.25rem;
  box-shadow: rgb(33, 43, 56) -0.1875rem 0.1875rem 0.9375rem;
  border-radius: 0.9375rem;
  overflow: hidden;
`;

const Wrapper = styled.div`
  position: relative;
  width: ${POPUP_WIDTH};
  height: ${POPUP_HEIGHT};
  background-color: ${Theme.colors.WHITE};
  display: flex;
  flex-direction: column;
`;

const TitleHeader = styled.div`
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  min-height: 3.125rem;
  background-color: ${Theme.colors.B_2};
`;

const TitleText = styled.span`
  flex: 1;
  font-size: 1.0625rem;
  font-weight: 600;
  color: ${Theme.colors.WHITE};
`;

const CloseIconWrapper = styled.div`
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 0.625rem;
  transform: translateY(-50%);
  z-index: 1;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  padding: 1rem 1.25rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ContentText = styled.span<{ fontSize: string }>`
  font-size: ${({ fontSize }) => fontSize};
  white-space: pre-wrap;
`;

const ImgList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`;
